import { Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, NavigationExtras, Router } from "@angular/router";
import { NGXLogger } from "ngx-logger";
import { Table } from "primeng/table";
import { Question } from "src/app/models/question.model";
import { Quiz } from "src/app/models/quiz.model";
import { QuestionsService } from "src/app/services/questions.service";
import { QuizesService } from "src/app/services/quizes.service";
import {
  GeneratedQuestion,
  QuizGenService,
} from "src/app/services/quiz-gen.service";
import { ChapterSectionsService } from "src/app/services/chapter-sections.service";
import { ChapterSection } from "src/app/models/chapter-sections.model";
import { ChaptersService } from "src/app/services/chapters.service";

type GeneratedSuggestion = GeneratedQuestion & { selected?: boolean };

@Component({
  selector: "app-questions-list",
  templateUrl: "./questions-list.component.html",
  styleUrls: ["./questions-list.component.css"],
})
export class QuestionsListComponent implements OnInit {
  @ViewChild("dt") private dt: Table;

  first = 0;
  rows = 15;
  sortField;
  sortOrder;
  filters;
  totalRecords = 0;

  displayDialog: boolean;

  quiz: Quiz;

  selectedQuestion: Question;

  newQuestion: boolean;

  questions: Question[];
  question: Question;

  cols: any[];

  errorMessage;

  questionForm = new FormGroup({
    QuestionText: new FormControl("", Validators.required),
    questiontype: new FormControl("", Validators.required),
  });

  loading = true;
  courseId: number;

  // --- AI question generation ---
  genDialog = false;
  genLoading = false;
  genSectionsLoading = false;
  genCount = 5;
  genNotes = "";
  genError: string;
  /** True when this quiz's chapter is a final chapter (name contains FINAL). */
  isFinalQuiz = false;
  /** Final quizzes: content-chapter dropdown (with an "All chapters" option). */
  chapterOptions: { label: string; value: number }[] = [];
  selectedChapterId: number = null;
  /** All sections of the currently-loaded chapter (resolves header children). */
  private allSections: ChapterSection[] = [];
  /** Top-level sections (parentId null) shown as the sections dropdown. */
  headers: ChapterSection[] = [];
  headerOptions: { label: string; value: number }[] = [];
  selectedHeaderId: number = null;
  suggestions: GeneratedSuggestion[] = [];
  private shownTexts: string[] = [];

  constructor(
    private logger: NGXLogger,
    private questionsService: QuestionsService,
    private quizesService: QuizesService,
    private quizGenService: QuizGenService,
    private chapterSectionsService: ChapterSectionsService,
    private chaptersService: ChaptersService,
    public router: Router,
    private activateRoute: ActivatedRoute
  ) { }

  ngOnInit() {
    this.activateRoute.params.subscribe(async (params) => {
      if (params["courseId"]) {
        this.courseId = parseInt(params["courseId"]);
      }
      await this.quizesService.findByCourseIdAndQuizId(this.courseId, params["quizId"]).then(async (quiz) => {
        this.quiz = quiz;
        await this.loadQuestions();
      });
    });
    this.cols = [
      { field: "QuestionText", header: "Text" },
      { field: "questiontype", header: "Type" },
    ];
  }

  async loadQuestions() {
    if (!this.quiz) {
      return;
    }

    this.questions = await this.questionsService.findByQuiz(this.courseId, this.chapterId, this.quiz.QuizID);

    this.totalRecords = this.questions.length;
    this.loading = false;
  }

  gotoAnswerOptions(question) {
    let navigationExtras: NavigationExtras = {
      queryParams: { questionId: question.QuestionID },
      // fragment: 'anchor'
    };
    this.router.navigate([
      "admin", "courses", this.courseId, "quizzes",
      this.quiz.QuizID,
      "questions",
      this.question.QuestionID,
      "answerOptions",
    ]);
  }

  showDialogToAdd() {
    this.newQuestion = true;
    this.displayDialog = true;
    this.clearAll();
    this.question = {
      QuizID: this.quiz.QuizID,
      questiontype: "mch",
    } as Question;
  }

  save() {
    let p: Promise<any>;
    if (this.newQuestion) {
      p = this.questionsService.create(this.courseId, this.chapterId, this.quiz.QuizID, this.question);
    } else {
      p = this.questionsService.update(this.courseId, this.chapterId, this.quiz.QuizID, this.question.QuestionID, this.question);
    }

    return p
      .then(() => {
        this.loadQuestions();
        this.clearAll();
        this.displayDialog = false;
      })
      .catch((err) => {
        if (
          err &&
          err.status === 400 &&
          err.error &&
          err.error.error &&
          err.error.error.description
        ) {
          this.errorMessage = err.error.error.description;
        }
      });
  }

  onRowSelect(event) {
    this.newQuestion = false;
    this.clearAll();
    this.question = Object.assign({} as Question, event.data);
    this.loadQuestions();
    this.displayDialog = true;
  }

  clearAll() {
    this.question = {} as Question;
    this.errorMessage = null;
  }

  delete() {
    let c = confirm("Are you sure you want to delete this question ?");
    if (c) {
      this.questionsService.delete(this.courseId, this.chapterId, this.quiz.QuizID, this.question.QuestionID).then(() => {
        this.loadQuestions();
        this.clearAll();
        this.displayDialog = false;
      });
    }
  }

  // --- AI question generation ---

  /** The chapter this quiz belongs to (tblquizzes.QuizLocation2). */
  get chapterId(): number {
    return this.quiz && this.quiz.QuizLocation2
      ? parseInt(this.quiz.QuizLocation2)
      : null;
  }

  async openGenerate() {
    this.genError = null;
    this.suggestions = [];
    this.allSections = [];
    this.headers = [];
    this.headerOptions = [];
    this.chapterOptions = [];
    this.selectedChapterId = null;
    this.selectedHeaderId = null;
    this.isFinalQuiz = false;
    this.shownTexts = [];
    this.genNotes = "";
    this.genCount = 5;
    this.genDialog = true;

    if (!this.chapterId) {
      this.genError =
        "This quiz is not linked to a chapter, so its sections can't be loaded.";
      return;
    }

    this.genSectionsLoading = true;
    try {
      const chapter = await this.chaptersService.getChapterBy(
        this.courseId,
        this.chapterId
      );
      this.isFinalQuiz = this.nameIsFinal(chapter && chapter.ChapterName);

      if (this.isFinalQuiz) {
        // Final quiz: pick a content chapter (or "All chapters"), then a section.
        const chapters =
          (await this.chaptersService.getChaptersBy(this.courseId)) || [];
        this.chapterOptions = [
          { label: "All chapters", value: null },
          ...chapters
            .filter((c) => !this.nameIsFinal(c.ChapterName))
            .map((c) => ({ label: c.ChapterName, value: c.ID })),
        ];
        this.selectedChapterId = null; // default: all chapters
      } else {
        // Normal quiz: section header dropdown for the quiz's own chapter.
        await this.loadChapterSections(this.chapterId, false);
        if (this.headers.length) {
          this.selectedHeaderId = this.headers[0].id;
        } else {
          this.genError = "No section headers found for this chapter.";
        }
      }
    } catch (err) {
      this.genError = this.extractError(err) || "Could not load chapter sections.";
      this.logger.error("Loading chapter sections failed", err);
    } finally {
      this.genSectionsLoading = false;
    }
  }

  private nameIsFinal(name: string): boolean {
    return !!name && name.trim().toUpperCase().indexOf("FINAL") !== -1;
  }

  /** Final quizzes: load the chosen content chapter's section headers. */
  async onChapterChange() {
    this.headers = [];
    this.headerOptions = [];
    this.allSections = [];
    this.selectedHeaderId = null;
    if (!this.selectedChapterId) {
      return; // "All chapters" -> no section dropdown
    }
    this.genSectionsLoading = true;
    try {
      await this.loadChapterSections(this.selectedChapterId, true);
    } catch (err) {
      this.genError = this.extractError(err) || "Could not load chapter sections.";
      this.logger.error("Loading chapter sections failed", err);
    } finally {
      this.genSectionsLoading = false;
    }
  }

  /** Load a chapter's sections and build the header dropdown options. */
  private async loadChapterSections(chapterId: number, withAllOption: boolean) {
    this.allSections =
      (await this.chapterSectionsService.findAll(this.courseId, chapterId)) || [];
    // parentId can be a number, null, or a string ("0" for bigint columns), so
    // compare numerically - otherwise headers with parentId "0" are missed and
    // the dropdown shows "No section headers found for this chapter."
    this.headers = this.allSections.filter((s) => !Number(s.parentId));
    const headerOpts = this.headers.map((h) => ({
      label: h.name || `Section ${h.id}`,
      value: h.id,
    }));
    this.headerOptions = withAllOption
      ? [{ label: "All sections", value: null }, ...headerOpts]
      : headerOpts;
    if (withAllOption) {
      this.selectedHeaderId = null; // default: all sections in the chapter
    }
  }

  /**
   * The sections that make up the selected header's content: its child sections
   * (parentId === header id), falling back to the header itself if it has none.
   */
  private sectionsForSelectedHeader(): ChapterSection[] {
    if (!this.selectedHeaderId) {
      return [];
    }
    const children = this.allSections.filter(
      (s) => Number(s.parentId) === Number(this.selectedHeaderId)
    );
    if (children.length) {
      return children;
    }
    const header = this.allSections.find((s) => s.id === this.selectedHeaderId);
    return header ? [header] : [];
  }

  /** First-round generation (replaces any current suggestions). */
  generate() {
    this.runGenerate(false);
  }

  /** Ask for more, avoiding everything already shown. */
  generateMore() {
    this.runGenerate(true);
  }

  /** Whether the current selection is enough to generate. */
  canGenerate(): boolean {
    if (this.genSectionsLoading) {
      return false;
    }
    // Final quiz: always allowed (no chapter = whole course).
    if (this.isFinalQuiz) {
      return true;
    }
    // Normal quiz: a section header must be selected.
    return !!this.selectedHeaderId;
  }

  /**
   * Build the context selection for the API:
   *  - normal quiz: the selected header's child section ids
   *  - final quiz: no chapter -> {} (whole course); chapter only -> {chapterId};
   *    chapter + header -> {chapterId, sectionIds}
   */
  private buildContextBody(): {
    chapterId?: number;
    sectionIds?: number[];
  } {
    if (!this.isFinalQuiz) {
      return { sectionIds: this.sectionsForSelectedHeader().map((s) => s.id) };
    }
    if (!this.selectedChapterId) {
      return {}; // all chapters
    }
    if (!this.selectedHeaderId) {
      return { chapterId: this.selectedChapterId }; // all sections in the chapter
    }
    return {
      chapterId: this.selectedChapterId,
      sectionIds: this.sectionsForSelectedHeader().map((s) => s.id),
    };
  }

  private async runGenerate(more: boolean) {
    if (!this.canGenerate()) {
      this.genError = "Select a section header to use as context.";
      return;
    }
    const contextBody = this.buildContextBody();
    this.genError = null;
    this.genLoading = true;
    try {
      const result = await this.quizGenService.generate(
        this.courseId,
        this.chapterId,
        this.quiz.QuizID,
        {
          ...contextBody,
          count: this.genCount,
          questionType: "mch",
          feedback: this.shownTexts.length
            ? { avoidQuestions: this.shownTexts, notes: this.genNotes || undefined }
            : this.genNotes
            ? { notes: this.genNotes }
            : undefined,
        }
      );

      const fresh: GeneratedSuggestion[] = (result.suggestions || []).map((s) => ({
        ...s,
        selected: true,
      }));

      this.suggestions = more ? [...this.suggestions, ...fresh] : fresh;
      this.shownTexts = this.suggestions.map((s) => s.questionText);

      if (!fresh.length) {
        this.genError = "No questions were generated. Try adjusting the notes or count.";
      }
    } catch (err) {
      this.genError = this.extractError(err) || "Generation failed. Please try again.";
      this.logger.error("Quiz generation failed", err);
    } finally {
      this.genLoading = false;
    }
  }

  hasSelected(): boolean {
    return this.suggestions.some((s) => s.selected);
  }

  async publishSelected() {
    const chosen = this.suggestions.filter((s) => s.selected);
    if (!chosen.length) {
      return;
    }
    this.genError = null;
    this.genLoading = true;
    try {
      const payload: GeneratedQuestion[] = chosen.map((s) => ({
        questionText: s.questionText,
        questionType: s.questionType || "mch",
        options: s.options,
      }));
      await this.quizGenService.publish(
        this.courseId,
        this.chapterId,
        this.quiz.QuizID,
        payload
      );
      this.genDialog = false;
      this.suggestions = [];
      this.shownTexts = [];
      await this.loadQuestions();
    } catch (err) {
      this.genError = this.extractError(err) || "Publish failed. Please try again.";
      this.logger.error("Quiz publish failed", err);
    } finally {
      this.genLoading = false;
    }
  }

  private extractError(err): string {
    return err && err.error && err.error.error && err.error.error.description
      ? err.error.error.description
      : null;
  }
}
